import { Reveal, MaskLineInView } from "../Reveal";
import { useLanguage } from "../../i18n/LanguageContext";
import { Check, KeyRound, LifeBuoy, ArrowUpRight } from "lucide-react";

export const Pricing = () => {
  const { t } = useLanguage();
  const p = t.pricing;

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-36 bg-cream">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="flex items-center gap-3 mb-8">
          <span className="dim-line text-forest" />
          <span className="micro text-forest/70">{p.label}</span>
        </div>
        <h2 className="font-display text-5xl md:text-7xl tracking-tight text-charcoal max-w-3xl">
          <MaskLineInView>{p.heading[0]}</MaskLineInView>
          <MaskLineInView delay={0.1} className="text-forest">{p.heading[1]}</MaskLineInView>
        </h2>
      </div>

      <div className="staff-rule my-12" />

      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* The Build */}
        <Reveal className="grid lg:grid-cols-12 gap-10 rounded-3xl bg-forest text-cream p-8 md:p-14 mb-8">
          <div className="lg:col-span-5">
            <span className="micro text-gold">{p.buildTag}</span>
            <div className="mt-6 flex items-end gap-3">
              <span className="font-display text-6xl md:text-7xl leading-none">{t.nav.quote}</span>
            </div>
            <p className="mt-6 text-cream/70 max-w-sm leading-relaxed">{p.buildDesc}</p>
            <a href="#contact" data-testid="pricing-build-cta" className="mt-8 btn-block bg-gold text-charcoal hover:bg-cream">
              {p.buildCta}
            </a>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-4 lg:pl-10 lg:border-l border-cream/15">
            {p.includes.map((f) => (
              <div key={f} className="flex items-center gap-3 py-2 border-b border-cream/10">
                <Check className="h-4 w-4 text-gold shrink-0" />
                <span className="text-cream/85">{f}</span>
              </div>
            ))}
          </div>
        </Reveal>

        {/* À la carte — the bundle above is the headline offer, but not everyone
            needs all three, and hiding the individual prices reads as evasive. */}
        <Reveal delay={0.05} className="mt-16 mb-8 text-center">
          <span className="micro text-forest/60 font-semibold">{p.alaCarteLabel}</span>
        </Reveal>

        {/* Each card is itself the link. Spelling out "Get a quote" on all three,
            in a section that already carries the same call twice above, just
            made the page repeat itself — one arrow says it instead. */}
        <div className="grid md:grid-cols-3 gap-6 items-start">
          {p.alaCarte.map((item, i) => (
            <Reveal key={item.name} delay={i * 0.08}>
              <a
                href="#contact"
                data-testid={`ala-carte-${i}`}
                aria-label={`${item.name} — ${t.nav.quote}`}
                className="group flex h-full flex-col rounded-3xl border border-line bg-white p-8 transition-transform duration-500 hover:-translate-y-1"
              >
                <div className="flex items-start justify-between gap-4">
                  <span className="micro text-charcoal/50">{item.name}</span>
                  <ArrowUpRight
                    aria-hidden="true"
                    className="h-4 w-4 shrink-0 text-charcoal/30 transition-all duration-300 group-hover:text-forest group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </div>
                <p className="mt-4 text-charcoal/60 leading-relaxed">{item.tagline}</p>
                {item.note && (
                  <p className="mt-5 pt-5 border-t border-line text-sm text-charcoal/50">{item.note}</p>
                )}
              </a>
            </Reveal>
          ))}
        </div>

        {/* Ownership & support reassurance */}
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          <Reveal data-testid="ownership-callout" className="rounded-3xl border border-line bg-white p-8 md:p-10">
            <KeyRound className="h-7 w-7 text-forest mb-6" strokeWidth={1.5} />
            <h3 className="font-display text-3xl text-charcoal">{p.ownershipTitle}</h3>
            <p className="mt-4 text-charcoal/65 leading-relaxed">{p.ownershipBody}</p>
          </Reveal>
          <Reveal delay={0.08} data-testid="support-callout" className="rounded-3xl border border-line bg-white p-8 md:p-10">
            <LifeBuoy className="h-7 w-7 text-forest mb-6" strokeWidth={1.5} />
            <h3 className="font-display text-3xl text-charcoal">{p.supportTitle}</h3>
            <p className="mt-4 text-charcoal/65 leading-relaxed">{p.supportBody}</p>
          </Reveal>
        </div>

      </div>
    </section>
  );
};
